// =============================================================================
// AppointmentTooltip - Tooltip dettagliato per appuntamento
// =============================================================================

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { parseISO, differenceInMinutes } from 'date-fns';
import { Briefcase, User, Clock, Timer, Users, FileText } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { getServiceColor, getContrastTextColor } from '../utils/color.utils';
import { formatAttributeKey, formatAttributeValue } from '../utils/grid.utils';
import type { AppointmentTooltipProps } from '../types/scheduler.types';
import { useTenantTimeZone } from '../../../store/tenantStore';
import { browserTimeZone, timeStringInTz } from '../../../lib/tenantTime';
import { useShowViewerLocalTimes } from '../../../store/schedulerStore';

export function AppointmentTooltip({ appointment, visible, bufferBeforeMinutes = 0, bufferAfterMinutes = 0, anchorRef }: AppointmentTooltipProps) {
  const { t } = useTranslation('scheduler');
  const timeZone = useTenantTimeZone();
  // Riferimento incrociato per chi lavora da un altro fuso (toggle nel banner)
  const showViewerLocal = useShowViewerLocalTimes();
  const viewerTimeZone = browserTimeZone();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!visible || !anchorRef?.current) {
      setPosition(null);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const tooltipWidth = 280;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Preferisci destra dell'appuntamento, altrimenti sinistra
    let left = rect.right + 8;
    if (left + tooltipWidth > viewportWidth) {
      left = rect.left - tooltipWidth - 8;
    }
    left = Math.max(8, Math.min(left, viewportWidth - tooltipWidth - 8));
    
    let top = rect.top;
    if (top + 300 > viewportHeight) {
      top = Math.max(8, viewportHeight - 300);
    }
    
    setPosition({ top, left });
  }, [visible, anchorRef]);

  if (!visible || !position) return null;
  
  const start = parseISO(appointment.scheduledStart);
  const end = parseISO(appointment.scheduledEnd);
  const serviceColor = getServiceColor(appointment.serviceDefinitionId, appointment.serviceColor);
  
  return createPortal(
    <div 
      className="fixed pointer-events-none animate-in fade-in slide-in-from-left-2 duration-150"
      style={{ minWidth: '280px', top: position.top, left: position.left, zIndex: 9999 }}
    >
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
        {/* Header colorato */}
        <div className="px-3 py-2" style={{ backgroundColor: serviceColor }}>
          <div className="flex items-center gap-2" style={{ color: getContrastTextColor(serviceColor) }}>
            <Briefcase className="w-4 h-4" />
            <span className="font-semibold text-sm">{appointment.serviceName}</span>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-3 space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-700">
            <User className="w-4 h-4 text-gray-400" />
            <span className="font-medium">{appointment.customerName}</span>
          </div>

          {/* Occupazione eventi di gruppo */}
          {(appointment.maxClients ?? 1) > 1 && (
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-xs">
                Partecipanti: <span className="font-medium">{appointment.participantCount ?? 1}/{appointment.maxClients}</span>
              </span>
            </div>
          )}

          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-4 h-4 text-gray-400" />
            <span>
              {timeStringInTz(start, timeZone)} - {timeStringInTz(end, timeZone)}
              {showViewerLocal && (
                <span className="ml-1.5 opacity-70">
                  ({timeStringInTz(start, viewerTimeZone)} - {timeStringInTz(end, viewerTimeZone)}{' '}
                  {t('timeZone.localSuffix')})
                </span>
              )}
              <span className="text-gray-400 ml-1">
                ({differenceInMinutes(end, start)} min)
              </span>
            </span>
          </div>
          
          {(bufferBeforeMinutes > 0 || bufferAfterMinutes > 0) && (
            <div className="flex items-center gap-2 text-gray-500">
              <Timer className="w-4 h-4 text-gray-400" />
              <span className="text-xs">
                {bufferBeforeMinutes > 0 && <span>Buffer prima: {bufferBeforeMinutes} min</span>}
                {bufferBeforeMinutes > 0 && bufferAfterMinutes > 0 && ' · '}
                {bufferAfterMinutes > 0 && <span>Buffer dopo: {bufferAfterMinutes} min</span>}
              </span>
            </div>
          )}
          
          {appointment.allocations && appointment.allocations.length > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-2">
                <Users className="w-3.5 h-3.5" />
                <span>Risorse assegnate</span>
              </div>
              <div className="space-y-2">
                {appointment.allocations.map(alloc => (
                  <div key={alloc.id} className="bg-gray-50 rounded-md p-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800 text-xs">{alloc.resourceName}</span>
                      <span className="text-xs text-gray-400">({alloc.resourceTypeName})</span>
                    </div>
                    {alloc.resourceAttributes && Object.keys(alloc.resourceAttributes).length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(alloc.resourceAttributes).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-1 text-xs text-gray-500">
                            <span className="text-gray-400">{formatAttributeKey(key)}:</span>
                            <span className="text-gray-600">{formatAttributeValue(value)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {appointment.notes && (
            <div className="flex items-start gap-2 text-gray-600 pt-2 border-t">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-xs italic">{appointment.notes}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-xs text-gray-400">Stato:</span>
            <span className={cn(
              'px-2 py-0.5 rounded-full text-xs font-medium',
              appointment.status === 'CONFIRMED' && 'bg-green-100 text-green-700',
              appointment.status === 'PENDING' && 'bg-yellow-100 text-yellow-700',
              appointment.status === 'IN_PROGRESS' && 'bg-blue-100 text-blue-700',
              appointment.status === 'COMPLETED' && 'bg-gray-100 text-gray-700',
              appointment.status === 'CANCELLED' && 'bg-red-100 text-red-700',
            )}>
              {appointment.status}
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
